/* ==========================================================================
   RAAGA LP ENGINE — shared by every landing page.
   Vanilla JS. No build step, no framework dependency.
   Reads window.RAAGA_LP_CONFIG and renders the whole page into #lp-root.
   ========================================================================== */

(function () {
  const CFG = window.RAAGA_LP_CONFIG;
  if (!CFG) {
    console.error('RAAGA_LP_CONFIG not found. Make sure config loads before lp-engine.js');
    return;
  }

  // Shopify Theme Editor reloads this section's <script> tag on every settings
  // change without a full page reload — tear down the previous instance first
  // so listeners/intervals/observers don't pile up and freeze the tab.
  if (window.__rpLpEngineInstance) {
    window.__rpLpEngineInstance.cleanup();
  }

  const state = {
    annIdx: 0,
    variant: CFG.hero.variants.length ? (CFG.hero.variants.find(v => v.default) || CFG.hero.variants[0]).id : null,
    cartCount: 0,
    showAddedToast: false,
    showSticky: false,
    resultStage: 0,
    heroImageIndex: 0,
    openFaq: 0,
    flippedIngredient: null,
    routineAdds: {},
  };

  function setState(patch) {
    Object.assign(state, patch);
    render();
  }

  const inr = (n) => '₹' + Number(n).toLocaleString('en-IN');
  const esc = (s) => (s == null ? '' : String(s));

  function currentVariant() {
    return CFG.hero.variants.find(v => v.id === state.variant) || CFG.hero.variants[0] || { id: '', label: '', sub: '', price: 0, mrp: null };
  }

  function goToCheckout(variantId) {
    const mode = (CFG.checkout && CFG.checkout.mode) || 'demo';
    if (mode === 'shopify') {
      const map = CFG.checkout.variantIdMap || {};
      const shopifyVariantId = map[variantId];
      if (!shopifyVariantId) {
        console.warn('No Shopify variant ID configured for', variantId, '— falling back to demo toast.');
        setState({ cartCount: state.cartCount + 1, showAddedToast: true });
        setTimeout(() => setState({ showAddedToast: false }), 1800);
        return;
      }
      const domain = CFG.checkout.shopifyDomain.replace(/\/$/, '');
      window.location.href = `${domain}/cart/${shopifyVariantId}:1`;
      return;
    }
    setState({ cartCount: state.cartCount + 1, showAddedToast: true });
    clearTimeout(window.__rp_toast_t);
    window.__rp_toast_t = setTimeout(() => setState({ showAddedToast: false }), 1800);
  }

  const ICONS = {
    sun: '<circle cx="12" cy="12" r="4.5"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/>',
    sparkle: '<path d="M12 3l1.9 5.6L19.5 10l-4.7 3.4 1.7 5.6L12 15.6 7.5 19l1.7-5.6L4.5 10l5.6-1.4z"/>',
    drop: '<path d="M12 2.5S5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.5 12 2.5z"/>',
    check: '<path d="M20 6L9 17l-5-5"/>',
    brief: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
    cap: '<path d="M22 10L12 5 2 10l10 5 10-5z"/><path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5"/>',
    users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>',
    star: '<path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6z"/>',
    chevronLeft: '<path d="M15 18l-6-6 6-6"/>',
    chevronRight: '<path d="M9 18l6-6-6-6"/>',
    bag: '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>',
  };
  function icon(name, size = 22, strokeWidth = 1.9) {
    const p = ICONS[name] || ICONS.sparkle;
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
  }
  function stars(count = 5, size = 15) {
    let out = '';
    for (let i = 0; i < 5; i++) {
      out += `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${i < count ? 'var(--rp-rating-star)' : 'none'}" stroke="var(--rp-rating-star)" stroke-width="${i < count ? 0 : 1.5}">${ICONS.star}</svg>`;
    }
    return out;
  }

  function imageSlot(img, aspect = '1/1', placeholderLabel = 'Image needed') {
    const src = img && img.src;
    const alt = (img && img.alt) || '';
    if (src) {
      return `<img src="${esc(src)}" alt="${esc(alt)}" style="width:100%;height:100%;object-fit:cover;display:block" />`;
    }
    return `<div class="rp-placeholder" style="aspect-ratio:${aspect}"><span>${esc(placeholderLabel)}</span></div>`;
  }

  function renderAnnouncementBar() {
    const msg = CFG.announcementBar.messages[state.annIdx % CFG.announcementBar.messages.length];
    return `<div class="rp-announcement"><span class="rp-dot"></span><span>${esc(msg)}</span></div>`;
  }

  function renderHeader() {
    const h = CFG.header;
    const nav = h.nav.map(n => `<a class="rp-nav-item" href="${esc(n.link || '#')}">${esc(n.label)}</a>`).join('');
    const logo = h.logoImage && h.logoImage.src
      ? `<img src="${esc(h.logoImage.src)}" alt="${esc(h.logoImage.alt)}" class="rp-logo-img" />`
      : `${esc(h.logoPrefix)}<span class="rp-logo-dot">•</span><span class="rp-logo-suffix">${esc(h.logoSuffix)}</span>`;
    return `
    <header class="rp-header">
      <div class="rp-container rp-header-inner">
        <a class="rp-logo" href="${esc(h.logoLink || '/')}">${logo}</a>
        <nav class="rp-nav">${nav}</nav>
<div class="rp-header-right">
          <button class="rp-header-cta" data-action="trigger-fastrr">${esc(CFG.hero.ctaSecondaryLabel)}</button>
        </div>
      </div>
    </header>`;
  }

  function currentHeroImages() {
    const v = currentVariant();
    if (v.images && v.images.length) return v.images;
    if (CFG.hero.image && CFG.hero.image.src) return [CFG.hero.image];
    return [];
  }

  function renderHero() {
    const h = CFG.hero;
    if (!h.variants.length) {
      return `<section id="hero-fold" class="rp-hero"><div class="rp-container rp-center" style="padding:60px 20px;color:#5B6E68;font-weight:700;">Add at least one "Hero: product option" block in the Theme Editor to preview pricing and enable checkout.</div></section>`;
    }
    const v = currentVariant();
    const offPct = v.mrp ? Math.round((1 - v.price / v.mrp) * 100) : null;

    const benefitIcons = h.benefitIcons.map(b => `
      <div class="rp-benefit-tile">${icon(b.icon, 26)}<div class="rp-benefit-label">${esc(b.label)}</div></div>`).join('');

    const topBadges = h.topBadges.map((b, i) => `<span class="rp-tag ${i === 1 ? 'rp-tag--stock' : 'rp-tag--dark'}">${i === 1 ? '<span class="rp-stock-dot"></span>' : ''}${esc(b)}</span>`).join('');

    const headline = h.headlineLines.map((line, i) =>
      i === h.headlineAccentLine ? `<span class="rp-accent">${esc(line)}</span>` : esc(line)
    ).join(' ');

    const trustBadges = h.trustBadges.map(t => `<span class="rp-badge-chip">${esc(t)}</span>`).join('');

    const variants = h.variants.map(vr => {
      const selected = vr.id === state.variant;
      return `
      <button class="rp-variant ${selected ? 'is-selected' : ''}" data-action="select-variant" data-variant="${vr.id}">
        ${vr.badge ? `<span class="rp-variant-badge">${esc(vr.badge)}</span>` : ''}
        <span class="rp-variant-radio">${selected ? '<span></span>' : ''}</span>
        <span class="rp-variant-info">
          <span class="rp-variant-label">${esc(vr.label)}</span>
          <span class="rp-variant-sub" style="${vr.sub && vr.sub.toLowerCase().includes('save') ? 'color:var(--rp-in-stock);font-weight:700' : ''}">${esc(vr.sub || '')}</span>
        </span>
        <span class="rp-variant-price">${inr(vr.price)}</span>
      </button>`;
    }).join('');

    return `
    <section id="hero-fold" class="rp-hero">
      <div class="rp-container rp-hero-grid">
        <div class="rp-hero-rating-block">
          <div class="rp-rating-row">
            <span class="rp-stars">${stars(Math.round(h.rating.value))}</span>
            <span class="rp-rating-text">${h.rating.value}/5 · ${esc(h.rating.count)} reviews · ${esc(h.rating.note)}</span>
          </div>
        </div>
        <div class="rp-hero-title-block">
          <h1 class="rp-h1">${headline}</h1>
          <div class="rp-hero-mobile-product-name">${esc(v.label)}</div>
        </div>
        <div class="rp-hero-media">
          <div class="rp-hero-image">
            ${(() => {
              const imgs = currentHeroImages();
              const idx = Math.min(state.heroImageIndex, Math.max(imgs.length - 1, 0));
              const active = imgs[idx] || h.image;
              return `
                ${imageSlot(active, '1/1', 'Hero product image')}
                <div class="rp-hero-tags">${topBadges}</div>
                ${imgs.length > 1 ? `
                  <button class="rp-hero-arrow rp-hero-arrow--prev" data-action="hero-image-prev" aria-label="Previous image">${icon('chevronLeft', 18, 2.4)}</button>
                  <button class="rp-hero-arrow rp-hero-arrow--next" data-action="hero-image-next" aria-label="Next image">${icon('chevronRight', 18, 2.4)}</button>
                  <div class="rp-hero-dots">
                    ${imgs.map((_, i) => `<button class="rp-hero-dot ${i === idx ? 'is-active' : ''}" data-action="hero-image-dot" data-index="${i}" aria-label="Image ${i + 1}"></button>`).join('')}
                  </div>
                ` : ''}
              `;
            })()}
          </div>
          <div class="rp-benefit-row">${benefitIcons}</div>
        </div>
<div class="rp-hero-copy">
          <p class="rp-subhead">${v.subheadline || h.subheadline}</p>
          <div class="rp-badge-row">${trustBadges}</div>
          <div class="rp-price-row">
            <span class="rp-price">${inr(v.price)}</span>
            ${v.mrp ? `<span class="rp-mrp">${inr(v.mrp)}</span>` : ''}
            ${offPct ? `<span class="rp-save-chip">SAVE ${inr(v.mrp - v.price)} · ${offPct}% OFF</span>` : ''}
          </div>
          <div class="rp-tax-note">Inclusive of all taxes</div>
          <div class="rp-variant-list">${variants}</div>
          <div class="rp-cta-row" id="rp-cta-slot"></div>
          <div class="rp-delivery-note">${icon('check', 15, 2.4)} ${esc(h.deliveryNote)}</div>
        </div>
      </div>
    </section>`;
  }

  function renderProblem() {
    const p = CFG.problem;
    const pains = p.pains.map(pn => `
      <div class="rp-pain-card">
        <div class="rp-pain-mark">${esc(pn.mark)}</div>
        <div class="rp-pain-quote">${esc(pn.quote)}</div>
        <div class="rp-pain-why">${esc(pn.why)}</div>
      </div>`).join('');
    return `
    <section id="${esc(p.anchor)}" class="rp-section rp-section--dark">
      <div class="rp-container rp-center">
        <div class="rp-eyebrow rp-eyebrow--light">${esc(p.eyebrow)}</div>
        <h2 class="rp-h2 rp-h2--light">${esc(p.heading)}</h2>
        <div class="rp-pain-grid">${pains}</div>
        <div class="rp-bridge">${esc(p.bridge)}</div>
      </div>
    </section>`;
  }

  function renderProductStory() {
    const s = CFG.productStory;
    const diffs = s.differentiators.map(d => `<div class="rp-diff-row">${icon('check', 22, 2.6)}<span>${esc(d)}</span></div>`).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-story-grid">
        <div>
          <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
          <h2 class="rp-h2">${s.headingHtml}</h2>
          ${s.paragraphs.map(p => `<p class="rp-body-text">${p}</p>`).join('')}
          <div class="rp-diff-list">${diffs}</div>
          <div class="rp-callout">
            <div class="rp-callout-title">${esc(s.callout.title)}</div>
            <div class="rp-callout-body">${esc(s.callout.body)}</div>
          </div>
        </div>
        <div class="rp-story-media">${imageSlot(s.image, '4/5', 'Product story image')}</div>
      </div>
    </section>`;
  }

  function renderIngredients() {
    const s = CFG.ingredients;
    const cards = s.cards.map((c, i) => {
      const flipped = state.flippedIngredient === i;
      return `
      <button class="rp-ing-card" data-action="toggle-ingredient" data-index="${i}">
        <div class="rp-ing-top">
          <span class="rp-ing-num">0${i + 1}</span>
          <span class="rp-ing-hint">${esc(c.hint)}</span>
        </div>
        <div class="rp-ing-name">${esc(c.name)}</div>
        <div class="rp-ing-role">${esc(c.role)}</div>
        ${flipped ? `<div class="rp-ing-desc">${esc(c.desc)}</div>` : ''}
      </button>`;
    }).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-center">
        <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
        <h2 class="rp-h2">${esc(s.heading)}</h2>
        <p class="rp-sub-lede">${esc(s.sub)} <span class="rp-accent-text">Tap a card to see how it helps.</span></p>
      </div>
      <div class="rp-container rp-ing-grid">${cards}</div>
    </section>`;
  }

  function renderResults() {
    const s = CFG.results;
    if (!s.stages.length) {
      return `<section id="${esc(s.anchor)}" class="rp-section"><div class="rp-container rp-center" style="padding:40px 20px;color:#5B6E68;font-weight:700;">Add at least one "Results: stage" block in the Theme Editor.</div></section>`;
    }
    const cur = s.stages[state.resultStage];
    const tabs = s.stages.map((st, i) => `
      <button class="rp-stage-tab ${state.resultStage === i ? 'is-active' : ''}" data-action="select-stage" data-index="${i}">${esc(st.tag)}</button>`).join('');
    const pct = s.stages.length > 1 ? (state.resultStage / (s.stages.length - 1)) * 100 : 100;
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-results-grid">
        <div class="rp-results-media">${imageSlot(s.image, '1/1', 'Before / after comparison')}</div>
        <div>
          <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
          <h2 class="rp-h2">${esc(s.heading)}</h2>
          <div class="rp-stage-tabs">${tabs}</div>
          <div class="rp-stage-track"><div class="rp-stage-fill" style="width:${pct}%"></div></div>
          <div class="rp-stage-card">
            <div class="rp-stage-tag">${esc(cur.tag)}</div>
            <div class="rp-stage-title">${esc(cur.title)}</div>
            <div class="rp-stage-desc">${esc(cur.desc)}</div>
          </div>
          <div class="rp-disclaimer">${esc(s.disclaimer)}</div>
        </div>
      </div>
    </section>`;
  }

  function renderWhoFor() {
    const s = CFG.whoFor;
    const personas = s.personas.map(p => `
      <div class="rp-persona-card">
        <div class="rp-persona-icon">${icon(p.icon, 22)}</div>
        <div class="rp-persona-title">${esc(p.title)}</div>
        <div class="rp-persona-desc">${esc(p.desc)}</div>
      </div>`).join('');
    const skinTypes = s.skinTypes.map(t => `<span class="rp-badge-chip">${esc(t)}</span>`).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-center">
        <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
        <h2 class="rp-h2">${esc(s.heading)}</h2>
      </div>
      <div class="rp-container rp-persona-grid">${personas}</div>
      <div class="rp-container rp-suitable-row"><span class="rp-suitable-label">Suitable for</span><div class="rp-skin-chips">${skinTypes}</div></div>
    </section>`;
  }

  function renderHowToUse() {
    const s = CFG.howToUse;
    const steps = s.steps.map(st => `
      <div class="rp-step-row">
        <span class="rp-step-num">${esc(st.n)}</span>
        <div><div class="rp-step-title">${esc(st.t)}</div><div class="rp-step-desc">${esc(st.d)}</div></div>
      </div>`).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section rp-section--dark">
      <div class="rp-container rp-center">
        <div class="rp-eyebrow rp-eyebrow--light">${esc(s.eyebrow)}</div>
        <h2 class="rp-h2 rp-h2--light">${esc(s.heading)}</h2>
      </div>
      <div class="rp-container rp-howto-grid">
        <div class="rp-howto-media">${imageSlot(s.image, '4/3', 'How-to-use step guide')}</div>
        <div class="rp-howto-steps">
          ${steps}
          <div class="rp-protip"><strong>Pro tip —</strong> ${esc(s.proTip)}</div>
        </div>
      </div>
    </section>`;
  }

  function renderRoutineBuilder() {
    const s = CFG.routineBuilder;
    let total = 0;
    const items = s.items.map(it => {
      if (it.isCurrentProduct) { total += it.price; }
      else if (state.routineAdds[it.id]) { total += it.price; }
      const added = !!state.routineAdds[it.id];
      return `
      <div class="rp-routine-row">
        <div class="rp-routine-left">
          <span class="rp-routine-step">${esc(it.step)}</span>
          <div class="rp-routine-info">
            <div class="rp-routine-act">${esc(it.act)}</div>
            <div class="rp-routine-name">${esc(it.name)}</div>
          </div>
        </div>
        <div class="rp-routine-right">
          <span class="rp-routine-price">${inr(it.price)}</span>
          ${it.isCurrentProduct
            ? `<span class="rp-in-cart-chip">IN CART</span>`
            : `<button class="rp-routine-add ${added ? 'is-added' : ''}" data-action="toggle-routine" data-id="${it.id}">${added ? 'ADDED ✓' : 'ADD'}</button>`}
        </div>
      </div>`;
    }).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-narrow">
        <div class="rp-center">
          <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
          <h2 class="rp-h2">${esc(s.heading)}</h2>
        </div>
        <div class="rp-routine-list">${items}</div>
        <div class="rp-bundle-cta">
          <div>
            <div class="rp-bundle-title">${esc(s.bundleNote.title)}</div>
            <div class="rp-bundle-sub">${esc(s.bundleNote.sub)}</div>
          </div>
          <div class="rp-bundle-right">
            <span class="rp-bundle-total">${inr(total)}</span>
            <button class="rp-btn rp-btn--dark-on-accent">${esc(s.bundleCtaLabel)}</button>
          </div>
        </div>
      </div>
    </section>`;
  }

  function renderTrust() {
    const s = CFG.trust;
    const badges = s.badges.map(b => `
      <div class="rp-cert-card">
        <div class="rp-cert-icon">${icon('shield', 20)}</div>
        <div class="rp-cert-label">${esc(b)}</div>
      </div>`).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container">
        <div class="rp-center">
          <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
          <h2 class="rp-h2">${esc(s.heading)}</h2>
        </div>
        <div class="rp-cert-grid">${badges}</div>
        <div class="rp-story-panel">
          <div class="rp-story-panel-title">${esc(s.story.title)}</div>
          <p class="rp-story-panel-body">${s.story.bodyHtml}</p>
          <div class="rp-story-panel-meta">${esc(s.story.meta)}</div>
        </div>
      </div>
    </section>`;
  }

  function renderReviews() {
    const s = CFG.reviews;
    const items = s.items.map(r => `
      <div class="rp-review-card">
        <div class="rp-review-stars">${stars(r.stars)}</div>
        <div class="rp-review-title">${esc(r.title)}</div>
        <div class="rp-review-body">${esc(r.body)}</div>
        <div class="rp-review-meta">${icon('check', 14, 2.6)} <strong>${esc(r.name)}</strong> · ${esc(r.meta)}</div>
      </div>`).join('');
    const ugc = (s.ugcImages || []).map(img => `<div class="rp-ugc-tile">${imageSlot(img, '1/1', 'UGC photo')}</div>`).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container">
        <div class="rp-reviews-head">
          <div>
            <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
            <h2 class="rp-h2">${esc(s.heading)}</h2>
          </div>
          <div class="rp-rating-block">
            <div class="rp-rating-big">${s.rating.value}</div>
            <div class="rp-rating-stars">${stars(Math.round(s.rating.value))}</div>
            <div class="rp-rating-caption">${esc(s.rating.caption)}</div>
          </div>
        </div>
        <div class="rp-review-grid">${items}</div>
        <div class="rp-review-summary">${esc(s.summary)}</div>
        ${ugc ? `<div class="rp-ugc-label">From the community</div><div class="rp-ugc-grid">${ugc}</div>` : ''}
      </div>
    </section>`;
  }

  function renderFaq() {
    const s = CFG.faq;
    const items = s.items.map((f, i) => {
      const open = state.openFaq === i;
      return `
      <div class="rp-faq-item">
        <button class="rp-faq-q" data-action="toggle-faq" data-index="${i}">
          <span>${esc(f.q)}</span>
          <span class="rp-faq-sign">${open ? '–' : '+'}</span>
        </button>
        ${open ? `<div class="rp-faq-a">${esc(f.a)}</div>` : ''}
      </div>`;
    }).join('');
    return `
    <section id="${esc(s.anchor)}" class="rp-section">
      <div class="rp-container rp-narrow">
        <div class="rp-center">
          <div class="rp-eyebrow">${esc(s.eyebrow)}</div>
          <h2 class="rp-h2">${esc(s.heading)}</h2>
        </div>
        <div class="rp-faq-list">${items}</div>
      </div>
    </section>`;
  }

  function renderFinalCta() {
    const s = CFG.finalCta;
    const v = currentVariant();
    return `
    <section id="${esc(s.anchor)}" class="rp-section rp-section--dark rp-final-cta">
      <div class="rp-container rp-center rp-narrow">
        <h2 class="rp-h2 rp-h2--light">${s.headingHtml}</h2>
        <p class="rp-final-sub">${esc(s.sub)}</p>
        <div class="rp-final-card">
          <div class="rp-final-product">${esc(s.productName)}</div>
          <div class="rp-final-price-row">
            <span class="rp-final-price">${inr(v.price)}</span>
            ${v.mrp ? `<span class="rp-mrp">${inr(v.mrp)}</span>` : ''}
            ${v.mrp ? `<span class="rp-save-chip">${Math.round((1 - v.price / v.mrp) * 100)}% OFF</span>` : ''}
          </div>
<div class="rp-cta-row">
            <button class="rp-btn rp-btn--light" data-action="trigger-fastrr">BUY NOW</button>
          </div>
        </div>
        <div class="rp-urgency">${esc(s.urgencyNote)}</div>
      </div>
    </section>`;
  }

  function renderFooter() {
    const s = CFG.footer;
    const links = (s.links || []).filter(l => l.label).map(l =>
      `<a href="${esc(l.link || '#')}" class="rp-footer-link">${esc(l.label)}</a>`).join('');
    return `
    <footer class="rp-footer">
      <div class="rp-footer-logo">${esc(CFG.header.logoPrefix)}<span class="rp-logo-dot">•</span>${esc(CFG.header.logoSuffix)}</div>
      <div>${esc(s.tagline)}</div>
      ${links ? `<div class="rp-footer-links">${links}</div>` : ''}
      <div class="rp-footer-copyright">${esc(s.copyright || ('© ' + s.year + ' Raaga Professional'))}</div>
    </footer>`;
  }

  function renderStickyBar() {
    const v = currentVariant();
    return `
    <div class="rp-sticky-bar${state.showSticky ? ' is-visible' : ''}">
      <div class="rp-sticky-thumb">DE·TAN</div>
      <div class="rp-sticky-info">
        <div class="rp-sticky-name">${esc(CFG.finalCta.productName)} · ${esc(v.label)}</div>
        <div class="rp-sticky-price">${inr(v.price)} ${v.mrp ? `<span class="rp-mrp">${inr(v.mrp)}</span>` : ''}</div>
      </div>
      <button class="rp-btn rp-btn--primary rp-sticky-btn" data-action="add-to-bag">ADD TO BAG</button>
    </div>`;
  }

  function renderToast() {
    if (!state.showAddedToast) return '';
    return `<div class="rp-toast">${icon('check', 17, 3)} Added to bag</div>`;
  }

function relocateFastrrWidget() {
    const slot = document.getElementById('rp-cta-slot');
    const widget = document.getElementById('rp-fastrr-slot');
    if (!slot || !widget) return;
    widget.style.display = '';
    if (widget.parentElement !== slot) {
      slot.appendChild(widget);
    }
    const v = currentVariant();
    const urlInput = document.getElementById('rp-fastrr-product-url');
    if (urlInput && v.productUrl) urlInput.value = v.productUrl;
    const variantIdInput = document.getElementById('rp-fastrr-variant-id');
    if (variantIdInput) {
      const map = (CFG.checkout && CFG.checkout.variantIdMap) || {};
      const realVariantId = map[state.variant];
      if (realVariantId) variantIdInput.value = realVariantId;
    }
  }

function render() {
    const root = document.getElementById('lp-root');
    // Fastrr's real button is only ever injected once by their script. If it's
    // currently living inside #lp-root (moved there by a previous render), pull
    // it out BEFORE wiping innerHTML below — otherwise it gets destroyed for good.
    const fastrrWidget = document.getElementById('rp-fastrr-slot');
    if (fastrrWidget && root.contains(fastrrWidget)) {
      fastrrWidget.style.display = 'none';
      document.body.appendChild(fastrrWidget);
    }
    root.innerHTML = `
      ${renderAnnouncementBar()}
      ${renderHeader()}
      ${renderHero()}
      ${renderProblem()}
      ${renderProductStory()}
      ${renderIngredients()}
      ${renderResults()}
      ${renderWhoFor()}
      ${renderHowToUse()}
      ${CFG.routineBuilder.enabled !== false ? renderRoutineBuilder() : ''}
      ${renderTrust()}
      ${renderReviews()}
      ${renderFaq()}
      ${renderFinalCta()}
      ${renderFooter()}
      ${renderStickyBar()}
      ${renderToast()}
    `;
    attachStickyObserver();
    relocateFastrrWidget();
  }

  function handleDocumentClick(e) {
    const el = e.target.closest('[data-action]');
    if (!el) return;
    const action = el.dataset.action;

    if (action === 'select-variant') {
      setState({ variant: el.dataset.variant, heroImageIndex: 0 });
    } else if (action === 'add-to-bag' || action === 'buy-now') {
      goToCheckout(state.variant);
    } else if (action === 'toggle-ingredient') {
      const i = Number(el.dataset.index);
      setState({ flippedIngredient: state.flippedIngredient === i ? null : i });
    } else if (action === 'select-stage') {
      setState({ resultStage: Number(el.dataset.index) });
    } else if (action === 'toggle-faq') {
      const i = Number(el.dataset.index);
      setState({ openFaq: state.openFaq === i ? -1 : i });
    } else if (action === 'toggle-routine') {
      const id = el.dataset.id;
      const next = { ...state.routineAdds, [id]: !state.routineAdds[id] };
      setState({ routineAdds: next });
} else if (action === 'scroll-to-buy') {
      document.getElementById('hero-fold')?.scrollIntoView({ behavior: 'smooth' });
    } else if (action === 'trigger-fastrr') {
      const realBtn = document.querySelector('#rp-fastrr-slot .shiprocket-headless button.sr-headless-checkout');
      if (realBtn) {
        realBtn.click();
      } else {
        document.getElementById('hero-fold')?.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (action === 'hero-image-dot') {
      setState({ heroImageIndex: Number(el.dataset.index) });
    } else if (action === 'hero-image-prev' || action === 'hero-image-next') {
      const imgs = currentHeroImages();
      const n = imgs.length || 1;
      const delta = action === 'hero-image-prev' ? -1 : 1;
      setState({ heroImageIndex: (state.heroImageIndex + delta + n) % n });
    }
  }
  document.addEventListener('click', handleDocumentClick);

  let announcementInterval = null;
  if (CFG.announcementBar.messages.length > 1) {
    announcementInterval = setInterval(() => {
      state.annIdx = (state.annIdx + 1) % CFG.announcementBar.messages.length;
      const msgEl = document.querySelector('.rp-announcement span:last-child');
      if (msgEl) msgEl.textContent = CFG.announcementBar.messages[state.annIdx];
    }, CFG.announcementBar.rotateMs || 4500);
  }

  let stickyObserver = null;

  function attachStickyObserver() {
    const el = document.getElementById('hero-fold');
    if (!el || !('IntersectionObserver' in window)) return;
    if (stickyObserver) stickyObserver.disconnect();
    stickyObserver = new IntersectionObserver((entries) => {
      const past = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      if (state.showSticky !== past) {
        state.showSticky = past;
        const bar = document.querySelector('.rp-sticky-bar');
        if (bar) bar.classList.toggle('is-visible', state.showSticky);
      }
    }, { threshold: 0 });
    stickyObserver.observe(el);
  }

  render();
  document.title = CFG.page.title;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', CFG.page.description);

  window.__rpLpEngineInstance = {
    cleanup: function () {
      document.removeEventListener('click', handleDocumentClick);
      if (announcementInterval) clearInterval(announcementInterval);
      if (stickyObserver) stickyObserver.disconnect();
    }
  };
})();